import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react";
import {
  Grid,
  Typography,
  List,
  ListItem,
  Dialog,
  AppBar,
  Toolbar,
  DialogContent,
  Slide,
  IconButton,
  Button,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  Box,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import EmailEditor from "react-email-editor";
import NewWaiverDialog from "./components/NewWaiverDialog";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const Waivers = () => {
  const waiverEditorRef = useRef(null);
  const [waivers, setWaivers] = useState([]);
  const [waiver, setWaiver] = useState("");
  const [nameDialogOpen, setNameDialogOpen] = useState(false);

  useEffect(() => {
    getWaivers();
  }, []);

  const getWaivers = async () => {
    const result = await request("waiver");
    if (result.success) {
      setWaivers(result.data);
    }
  };

  const getWaiver = async (id) => {
    setWaiver("");
    const result = await request(`waiver/?waiverId=${id}`);
    if (result.success && result.data) {
      setWaiver(result.data);
      waiverEditorRef.current?.editor.loadDesign(result.data.jsonBody);
    }
  };

  const handleSave = async (name, saveAs = false) => {
    const unlayer = waiverEditorRef.current?.editor;
    if (!unlayer) {
      console.warn("Unlayer editor not ready");
      return;
    }

    unlayer.exportHtml(async function (d) {
      const data = {
        title: saveAs ? name : waiver?.title,
        slug: waiver?.slug,
        termsHtml: d.html,
        jsonBody: d.design,
      };

      if (!saveAs && waiver?._id) {
        const body = {waiverId: waiver._id, update: data}
        
        const result = await request("waiver/update", "post", body);
        if (result.success) {
          toast.success("Waiver updated successfully");
          await getWaivers();
          await getWaiver(waiver._id);
        } else {
          toast("Failed to update waiver");
        }
      } else {
        const result = await request("waiver/create", "post", data);
        if (result.success) {
          toast.success("Waiver created successfully");
          await getWaivers();
          await getWaiver(result.waiverId);
        } else {
          toast("Failed to create waiver");
        }
      }
    });
  };

  const onReady = (unlayer) => {
    console.log("ready");
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Select
          value={waiver?._id || ""}
          onChange={(e) => {
            if (!e.target.value) {
              setWaiver("");
              waiverEditorRef.current?.editor.loadDesign({});
            } else {
              getWaiver(e.target.value);
            }
          }}
          size="small"
          sx={{ width: { xs: "100%", md: "60%" } }}
          displayEmpty
          renderValue={(selected) =>
            selected === ""
              ? "No waiver selected"
              : waivers.find((w) => w._id === selected)?.title || ""
          }
        >
          <MenuItem value="">No waiver selected</MenuItem>
          {waivers &&
            waivers.length > 0 &&
            waivers.map((w) => (
              <MenuItem key={w._id} value={w._id}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <span>{w.title}</span>
                  {w.event?.name && (
                    <span style={{ fontSize: "0.75rem", opacity: 0.6 }}>
                      Assigned to: {w.event.name}
                    </span>
                  )}
                </Box>
              </MenuItem>
            ))}
        </Select>
        <Button variant="contained" size="small" onClick={() => handleSave()}>
          Save
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setNameDialogOpen(true)}
        >
          Save As
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          label="Slug"
          size="small"
          sx={{ width: { xs: "100%", md: "80%" } }}
          value={waiver?.slug || ""}
          onChange={(e) =>
            setWaiver({
              ...waiver,
              slug: e.target.value.toLowerCase().replace(/\s+/g, "-"),
            })
          }
          error={/\s/.test(waiver?.slug || "")}
          helperText={
            /\s/.test(waiver?.slug || "")
              ? "Slug cannot contain spaces. They are automatically replaced with hyphens."
              : ""
          }
        />
        <TextField
          label="Title"
          size="small"
          sx={{ width: { xs: "100%", md: "80%" } }}
          value={waiver?.title || ""}
          onChange={(e) => setWaiver({ ...waiver, title: e.target.value })}
        />
      </Box>
      <Box>
        <EmailEditor
          ref={waiverEditorRef}
          onReady={onReady}
          options={{
            id: "editor-container",
            displayMode: "email",
            features: {
              preview: false,
            },
          }}
          style={{ height: "80vh" }}
        />
      </Box>
      <NewWaiverDialog
        open={nameDialogOpen}
        close={() => setNameDialogOpen(false)}
        save={(name) => handleSave(name, true)}
      />
    </Box>
  );
};

export default Waivers;
